/** Presentation-only helpers for upcoming game ticket cards — sport accents from sportTheme (WI-3). */
import {
  getSportLabel,
  getSportNeonAccent,
  getSportTheme,
} from '../constants/sportTheme';

export function getSportStripeGradient(sport) {
  return getSportTheme(sport).accent.stripe;
}

export function getSportLabelClass(sport) {
  return getSportTheme(sport).accent.label;
}

export function formatGameTime(iso) {
  if (!iso) return { date: '', time: '–' };
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { date: '', time: '–' };
    return {
      date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
    };
  } catch (_) {
    return { date: '', time: '–' };
  }
}

export function formatSportLabel(sport) {
  return getSportLabel(sport);
}

export function getSportNeonStyle(sport) {
  return getSportNeonAccent(sport);
}

/** Subtle hover glow for compact list rows — not full card neon. */
export function getSportRowHoverGlow(sport) {
  return getSportTheme(sport).accent.rowHover;
}

/** Human countdown for featured lock — e.g. "Starts in 3h 12m". */
export function formatStartsIn(startTimeIso) {
  if (!startTimeIso) return 'Time TBD';
  const t = new Date(startTimeIso).getTime();
  if (Number.isNaN(t)) return 'Time TBD';
  const diff = t - Date.now();
  if (diff <= 0) return 'Starting now';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `Starts in ${mins}m`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) {
    return remMins > 0 ? `Starts in ${hours}h ${remMins}m` : `Starts in ${hours}h`;
  }
  const days = Math.floor(hours / 24);
  return `Starts in ${days}d`;
}

/** Short team label for trending rows — last word or truncated name. */
export function formatShortTeamName(name) {
  const raw = (name || '').trim();
  if (!raw) return '—';
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length > 1) return parts[parts.length - 1];
  return raw.length > 14 ? `${raw.slice(0, 14)}…` : raw;
}

export function formatShortMatchup(teamA, teamB) {
  return `${formatShortTeamName(teamA)} vs ${formatShortTeamName(teamB)}`;
}

/** ESPN-style scoreboard abbrev — first word, up to 3 chars. */
export function formatScoreboardAbbrev(name) {
  const raw = (name || '').trim();
  if (!raw) return '—';
  const first = raw.split(/\s+/)[0] || raw;
  return first.slice(0, 3).toUpperCase();
}

/** Compact row status hint for upcoming games. */
export function getUpcomingStatusHint(startTimeIso) {
  if (!startTimeIso) return 'Upcoming';
  const t = new Date(startTimeIso).getTime();
  if (Number.isNaN(t)) return 'Upcoming';
  const diff = t - Date.now();
  if (diff <= 0) return 'Starting';
  if (diff <= 2 * 60 * 60 * 1000) return 'Starts soon';
  return 'Upcoming';
}

/** Abstract text initials — no logos or copyrighted marks. */
export function getTeamInitials(name) {
  const raw = (name || '').trim();
  if (!raw) return '—';
  const parts = raw.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return raw.slice(0, 2).toUpperCase();
}
